import React, { useState, useEffect } from 'react';

export interface DatePreset {
  label: string;
  value: 'last7d' | 'last30d' | 'last90d';
}

export interface FilterDateRangeProps {
  value: [Date | null, Date | null];
  onChange: (value: [Date | null, Date | null]) => void;
  presets?: DatePreset[];
}

const DEFAULT_PRESETS: DatePreset[] = [
  { label: 'Last 7d', value: 'last7d' },
  { label: 'Last 30d', value: 'last30d' },
  { label: 'Last 90d', value: 'last90d' },
];

/**
 * FilterDateRange — two date inputs + preset pills.
 */
export default function FilterDateRange({
  value,
  onChange,
  presets = DEFAULT_PRESETS,
}: FilterDateRangeProps) {
  const [fromDate, setFromDate] = useState<string>(
    value[0] ? value[0].toISOString().split('T')[0] : ''
  );
  const [toDate, setToDate] = useState<string>(
    value[1] ? value[1].toISOString().split('T')[0] : ''
  );

  useEffect(() => {
    setFromDate(value[0] ? value[0].toISOString().split('T')[0] : '');
    setToDate(value[1] ? value[1].toISOString().split('T')[0] : '');
  }, [value]);

  function handleFromChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newFrom = e.target.value;
    setFromDate(newFrom);
    onChange([
      newFrom ? new Date(newFrom) : null,
      value[1],
    ]);
  }

  function handleToChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newTo = e.target.value;
    setToDate(newTo);
    onChange([
      value[0],
      newTo ? new Date(newTo) : null,
    ]);
  }

  function applyPreset(preset: DatePreset) {
    const today = new Date();
    let from: Date;

    switch (preset.value) {
      case 'last7d':
        from = new Date(today);
        from.setDate(from.getDate() - 7);
        break;
      case 'last30d':
        from = new Date(today);
        from.setDate(from.getDate() - 30);
        break;
      case 'last90d':
        from = new Date(today);
        from.setDate(from.getDate() - 90);
        break;
    }

    onChange([from, today]);
  }

  return (
    <div className="min-h-[44px] flex flex-col gap-2">
      <label className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
        Date Range
      </label>
      <div className="flex items-end gap-2">
        <input
          type="date"
          value={fromDate}
          onChange={handleFromChange}
          aria-label="Date range start"
          className="flex-1 bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <span className="text-fg-muted">—</span>
        <input
          type="date"
          value={toDate}
          onChange={handleToChange}
          aria-label="Date range end"
          className="flex-1 bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </div>
      {presets.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {presets.map((preset) => (
            <button
              key={preset.value}
              onClick={() => applyPreset(preset)}
              className="text-xs px-2 py-1 rounded bg-bg-raised hover:bg-bg-elevated text-fg-primary focus:outline-none focus:ring-2 focus:ring-accent"
              aria-label={`Apply ${preset.label} preset`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
